import isEmpty from 'lodash/isEmpty';

export type AddressDisplayInput = {
  street_address?: string | null;
  apt_suite_unit?: string | null;
  city?: string | null;
  state_code?: string | null;
  zip_code?: string | null;
};

export type SearchAddressDisplayInput = AddressDisplayInput & {
  state?: { label?: string | null } | null;
};

/**
 * Preserves the established full-address string used by existing callers.
 * The leading space and `Unit#` punctuation are part of that contract.
 */
export const formatFullAddress = (address?: AddressDisplayInput | null): string => {
  if (isEmpty(address)) return '';

  const streetAddress = address?.street_address ? ` ${address.street_address}` : '';
  const aptSuite = address?.apt_suite_unit ? ` Unit# ${address.apt_suite_unit},` : '';
  const city = address?.city ? ` ${address.city}` : '';
  const stateCode = address?.state_code ? ` ${address.state_code}` : '';
  const zipCode = address?.zip_code ? ` ${address.zip_code}` : '';

  return `${streetAddress}${aptSuite}${city}${stateCode}${zipCode}`;
};

/**
 * Formats the saved account address for the Service Area search field.
 * The unit remains a supplied component; it is never parsed from the street.
 */
export const formatSearchAddress = (address?: SearchAddressDisplayInput | null): string => {
  const state = address?.state_code || address?.state?.label;
  const streetAddress = [
    address?.street_address,
    address?.apt_suite_unit ? `Unit# ${address.apt_suite_unit}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return [streetAddress, address?.city, [state, address?.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
};

/**
 * Preserves the coordinator vendor detail's existing full-address display and
 * placeholder semantics. This API variant intentionally has no unit segment.
 */
export const formatVendorCompanyAddress = (address?: AddressDisplayInput | null): string => {
  if (isEmpty(Object.values(address ?? {}).filter((value) => !isEmpty(value)))) return '-';

  return `${address?.street_address || ''}${address?.street_address ? ',' : ''} ${address?.city || ''}${
    address?.city ? ',' : ''
  } ${address?.state_code || ''} ${address?.zip_code || ''}`;
};

/** Preserves the compact city/state metadata shown on the vendor profile card. */
export const formatCityState = (address?: AddressDisplayInput | null): string =>
  `${address?.city || ''}${address?.city ? ',' : ''} ${address?.state_code || ''}`;
